import React, { useCallback, useState, useEffect } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  Panel,
  addEdge,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { nodeTypes } from './FlowchartNodes';
import { scriptToFlowchart, extractNodePositions } from '../lib/flowchartHelpers';
import { Plus, Download, Edit2, Settings } from 'lucide-react';
import Button from './Button';
import FlowchartNodeEditor from './FlowchartNodeEditor';
import FlowchartSettings from './FlowchartSettings';
import { produce } from 'immer';
import {
  createEmptyStage,
  createEmptyStep,
  createEmptyStepVar,
  getNextStageNumber,
  getNextStepNumber
} from '../lib/scriptHelpers';

export default function FlowchartEditor({ script, onChange, nodePositions = {} }) {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [editingNode, setEditingNode] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  
  // Загрузка настроек из localStorage
  const [layoutSettings, setLayoutSettings] = useState(() => {
    const saved = localStorage.getItem('flowchartSettings');
    return saved ? JSON.parse(saved) : {
      algorithm: 'dagre',
      direction: 'LR',
      density: 'standard',
      edgeType: 'default'
    };
  });

  const addNewStage = useCallback(() => {
    const updatedScript = produce(script, draft => {
      if (!draft.Main_Script) draft.Main_Script = { Stages: [] };
      const stageNumber = getNextStageNumber(draft);
      draft.Main_Script.Stages.push(createEmptyStage(stageNumber));
    });

    if (onChange) {
      onChange(updatedScript, {});
    }
  }, [script, onChange]);

  const addVariantToStage = useCallback((stageIndex) => {
    const updatedScript = produce(script, draft => {
      const stage = draft.Main_Script.Stages[stageIndex];
      if (stage) {
        if (!stage.Stage_Vars) stage.Stage_Vars = [];
        stage.Stage_Vars.push({
          Stage_Name: `Вариант ${stage.Stage_Vars.length + 1}`,
          Stage_Var_If: '',
          Steps: []
        });
      }
    });

    if (onChange) {
      onChange(updatedScript, {});
    }
  }, [script, onChange]);

  const addStepVarToStep = useCallback((stageIndex, variantIndex, stepIndex) => {
    const updatedScript = produce(script, draft => {
      const step = draft.Main_Script.Stages[stageIndex]?.Stage_Vars?.[variantIndex]?.Steps?.[stepIndex];
      if (step) {
        if (!step.Step_Vars) step.Step_Vars = [];
        step.Step_Vars.push(createEmptyStepVar(step.Step_Vars.length + 1));
      }
    });

    if (onChange) {
      onChange(updatedScript, {});
    }
  }, [script, onChange]);

  const addStepToVariant = useCallback((stageIndex, variantIndex) => {
    const updatedScript = produce(script, draft => {
      const variant = draft.Main_Script.Stages[stageIndex]?.Stage_Vars?.[variantIndex];
      if (variant) {
        if (!variant.Steps) variant.Steps = [];
        const stepNumber = getNextStepNumber({ Main_Script: { Stages: [{ Stage_Vars: [variant] }] } });
        variant.Steps.push(createEmptyStep(stepNumber));
      }
    });

    if (onChange) {
      onChange(updatedScript, {});
    }
  }, [script, onChange]);

  const autoLayoutNodes = useCallback(() => {
    if (onChange) {
      onChange(script, {});
    }
  }, [script, onChange]);

  const handleSettingsChange = useCallback((newSettings) => {
    setLayoutSettings(newSettings);
    localStorage.setItem('flowchartSettings', JSON.stringify(newSettings));
    // Сбрасываем сохраненные позиции для применения новой раскладки
    if (onChange) {
      onChange(script, {});
    }
  }, [script, onChange]);

  useEffect(() => {
    if (script) {
      scriptToFlowchart(script, nodePositions, layoutSettings).then(({ nodes: flowNodes, edges: flowEdges }) => {
      const nodesWithCallbacks = flowNodes.map(node => ({
        ...node,
        data: {
          ...node.data,
          onAddVariant: addVariantToStage,
          onAddStepVar: addStepVarToStep,
          onAddStep: addStepToVariant
        }
      }));
        setNodes(nodesWithCallbacks);
        setEdges(flowEdges);
        setHasChanges(false);
      });
    }
  }, [script, nodePositions, layoutSettings, addVariantToStage, addStepVarToStep, addStepToVariant]);

  const onNodeClick = useCallback((event, node) => {
    setSelectedNode(node);
  }, []);

  const onNodeDoubleClick = useCallback((event, node) => {
    if (['stageNode', 'stepNode', 'stepVarNode'].includes(node.type)) {
      setEditingNode(node);
    }
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  const handleNodesChange = useCallback((changes) => {
    onNodesChange(changes);
    setHasChanges(true);
  }, [onNodesChange]);

  const handleNodeDragStop = useCallback((event, node) => {
    setHasChanges(true);
  }, []);

  const saveNodePositions = useCallback(() => {
    const positions = extractNodePositions(nodes);
    if (onChange) {
      const updatedScript = produce(script, draft => {
        // Позиции сохраняются отдельно через ProjectEditor
      });
      // Вызываем onChange для сохранения позиций
      onChange(script, positions);
      setHasChanges(false);
    }
  }, [nodes, script, onChange]);

  const updateScriptNode = useCallback((node, formData) => {
    const updatedScript = produce(script, draft => {
      const { stageIndex, variantIndex, stepIndex, stepVarIndex } = node.data;

      if (node.type === 'stageNode' && stageIndex !== undefined) {
        const stage = draft.Main_Script.Stages[stageIndex];
        if (stage) {
          stage.Stage_Name = formData.Stage_Name;
          stage.Stage_Description = formData.Stage_Description;
          stage.Stage_Skip_If = formData.Stage_Skip_If;
        }
      } else if (node.type === 'stepNode' && stageIndex !== undefined && variantIndex !== undefined && stepIndex !== undefined) {
        const step = draft.Main_Script.Stages[stageIndex]?.Stage_Vars?.[variantIndex]?.Steps?.[stepIndex];
        if (step) {
          step.Step_Name = formData.Step_Name;
          step.Step_Description = formData.Step_Description;
          step.Step_Skip_If = formData.Step_Skip_If;
        }
      } else if (node.type === 'stepVarNode' && stageIndex !== undefined && variantIndex !== undefined && stepIndex !== undefined && stepVarIndex !== undefined) {
        const stepVar = draft.Main_Script.Stages[stageIndex]?.Stage_Vars?.[variantIndex]?.Steps?.[stepIndex]?.Step_Vars?.[stepVarIndex];
        if (stepVar) {
          stepVar.Step_Var_Name = formData.Step_Var_Name;
          stepVar.Step_Var_If = formData.Step_Var_If;
          if (!stepVar.Task_Settings) stepVar.Task_Settings = {};
          stepVar.Task_Settings.Task = formData.Task;
          stepVar.Task_Settings.Status = formData.Status;
          stepVar.Task_Settings.Approved_Phrases = formData.Approved_Phrases;
        }
      }
    });

    if (onChange) {
      onChange(updatedScript, extractNodePositions(nodes));
    }
    setEditingNode(null);
  }, [script, nodes, onChange]);

  const deleteScriptNode = useCallback((node) => {
    const updatedScript = produce(script, draft => {
      const { stageIndex, variantIndex, stepIndex, stepVarIndex } = node.data;

      if (node.type === 'stageNode' && stageIndex !== undefined) {
        draft.Main_Script.Stages.splice(stageIndex, 1);
      } else if (node.type === 'stepNode' && stageIndex !== undefined && variantIndex !== undefined && stepIndex !== undefined) {
        draft.Main_Script.Stages[stageIndex]?.Stage_Vars?.[variantIndex]?.Steps?.splice(stepIndex, 1);
      } else if (node.type === 'stepVarNode' && stageIndex !== undefined && variantIndex !== undefined && stepIndex !== undefined && stepVarIndex !== undefined) {
        draft.Main_Script.Stages[stageIndex]?.Stage_Vars?.[variantIndex]?.Steps?.[stepIndex]?.Step_Vars?.splice(stepVarIndex, 1);
      }
    });

    if (onChange) {
      onChange(updatedScript, extractNodePositions(nodes));
    }
    setEditingNode(null);
    setSelectedNode(null);
  }, [script, nodes, onChange]);

  const handleDownloadImage = useCallback(() => {
    const reactFlowElement = document.querySelector('.react-flow');
    if (!reactFlowElement) return;

    import('html-to-image').then(({ toPng }) => {
      toPng(reactFlowElement, {
        backgroundColor: '#ffffff',
        width: reactFlowElement.offsetWidth,
        height: reactFlowElement.offsetHeight,
      })
        .then((dataUrl) => {
          const link = document.createElement('a');
          link.download = 'flowchart.png';
          link.href = dataUrl;
          link.click();
        })
        .catch((err) => {
          console.error('Ошибка при экспорте:', err);
        });
    });
  }, []);

  return (
    <div className="h-[calc(100vh-250px)] w-full border rounded-lg overflow-hidden bg-gray-50 relative">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={handleNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={onNodeClick}
        onNodeDoubleClick={onNodeDoubleClick}
        onNodeDragStop={handleNodeDragStop}
        onPaneClick={onPaneClick}
        nodeTypes={nodeTypes}
        fitView
        attributionPosition="bottom-left"
        minZoom={0.1}
        maxZoom={1.5}
      >
        <Background color="#aaa" gap={16} />
        <Controls />
        <MiniMap
          nodeColor={(node) => {
            switch (node.type) {
              case 'stageNode':
                return '#3b82f6';
              case 'stepNode':
                return '#8b5cf6';
              case 'stepVarNode':
                return '#10b981';
              default:
                return '#64748b';
            }
          }}
          nodeStrokeWidth={3}
          zoomable
          pannable
        />
        
        <Panel position="top-right" className="bg-white rounded-lg shadow-md p-3 space-y-2 max-w-[200px]">
          <div className="text-xs font-semibold text-gray-700 mb-2">Управление</div>
          <Button
            onClick={addNewStage}
            size="sm"
            className="w-full text-xs"
          >
            <Plus size={14} className="mr-1" />
            Этап
          </Button>
          <Button
            onClick={autoLayoutNodes}
            size="sm"
            variant="outline"
            className="w-full text-xs"
            title="Автоматическая раскладка"
          >
            Раскладка
          </Button>
          <Button
            onClick={() => setShowSettings(true)}
            size="sm"
            variant="outline"
            className="w-full text-xs"
            title="Настройки отображения"
          >
            <Settings size={14} className="mr-1" />
            Настройки
          </Button>
          {hasChanges && (
            <Button
              onClick={saveNodePositions}
              size="sm"
              variant="outline"
              className="w-full text-xs bg-green-50 text-green-700 border-green-200"
            >
              Сохранить
            </Button>
          )}
          <div className="border-t pt-2 mt-2">
            <div className="text-xs font-semibold text-gray-700 mb-2">Легенда</div>
            <div className="flex items-center space-x-2 mb-1">
              <div className="w-3 h-3 bg-blue-500 rounded"></div>
              <span className="text-xs text-gray-600">Этапы</span>
            </div>
            <div className="flex items-center space-x-2 mb-1">
              <div className="w-3 h-3 bg-purple-500 rounded"></div>
              <span className="text-xs text-gray-600">Шаги</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-500 rounded"></div>
              <span className="text-xs text-gray-600">Варианты</span>
            </div>
          </div>
          <div className="border-t pt-2 mt-2 text-xs text-gray-500 space-y-1">
            <div>💡 Двойной клик - редактировать</div>
            <div>➕ Наведите на узел для кнопок</div>
          </div>
        </Panel>

        {selectedNode && (
          <Panel position="top-left" className="bg-white rounded-lg shadow-lg p-4 max-w-sm">
            <div className="space-y-2">
              <div className="flex items-center justify-between border-b pb-2">
                <h3 className="font-bold text-sm text-gray-900">
                  Детали узла
                </h3>
                {['stageNode', 'stepNode', 'stepVarNode'].includes(selectedNode.type) && (
                  <button
                    onClick={() => setEditingNode(selectedNode)}
                    className="text-blue-600 hover:text-blue-800"
                    title="Редактировать"
                  >
                    <Edit2 size={16} />
                  </button>
                )}
              </div>
              <div>
                <span className="text-xs font-semibold text-gray-600">Тип:</span>
                <span className="ml-2 text-xs text-gray-900">
                  {selectedNode.type === 'stageNode' ? 'Этап' : 
                   selectedNode.type === 'stepNode' ? 'Шаг' : 
                   selectedNode.type === 'stepVarNode' ? 'Вариант' : 'Узел'}
                </span>
              </div>
              <div>
                <span className="text-xs font-semibold text-gray-600">Название:</span>
                <p className="text-xs text-gray-900 mt-1">{selectedNode.data.label}</p>
              </div>
              {selectedNode.data.description && (
                <div>
                  <span className="text-xs font-semibold text-gray-600">Описание:</span>
                  <p className="text-xs text-gray-900 mt-1">{selectedNode.data.description}</p>
                </div>
              )}
              {selectedNode.data.condition && (
                <div>
                  <span className="text-xs font-semibold text-gray-600">Условие:</span>
                  <p className="text-xs text-gray-900 mt-1 italic">{selectedNode.data.condition}</p>
                </div>
              )}
              {selectedNode.data.task && (
                <div>
                  <span className="text-xs font-semibold text-gray-600">Задача:</span>
                  <p className="text-xs text-gray-900 mt-1">{selectedNode.data.task}</p>
                </div>
              )}
              {selectedNode.data.status && (
                <div>
                  <span className="text-xs font-semibold text-gray-600">Статус:</span>
                  <span className="ml-2 text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded">
                    {selectedNode.data.status}
                  </span>
                </div>
              )}
            </div>
          </Panel>
        )}
      </ReactFlow>

      <div className="absolute bottom-4 right-4 bg-white rounded-lg shadow-md p-2">
        <div className="text-xs text-gray-600 text-center mb-1">Экспорт</div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleDownloadImage}
          title="Скачать как изображение"
        >
          <Download className="h-4 w-4" />
        </Button>
      </div>

      {showSettings && (
        <FlowchartSettings
          settings={layoutSettings}
          onSettingsChange={handleSettingsChange}
          onClose={() => setShowSettings(false)}
        />
      )}

      {editingNode && (
        <FlowchartNodeEditor
          node={editingNode}
          onSave={updateScriptNode}
          onDelete={deleteScriptNode}
          onClose={() => setEditingNode(null)}
        />
      )}
    </div>
  );
}
